import {Component, Input, OnInit, SimpleChanges} from "@angular/core";
import {CommonModule} from "@angular/common";
import {FlexModule} from "@angular/flex-layout/flex";
import {
  booleanToYesNo,
  CorporateLoanApplication,
  getBrokerage,
  getDocFee,
  getInterestRate,
  getRbaRate,
  isInternalUser,
  LtvSelectionValueOptions,
  SecurityTypeSelectionValueOptions,
} from '@portal-workspace/grow-shared-library';
import {
  applicationToAdverseOnFile,
  applicationToBrokerageAmount,
  applicationToCrediRateAdjustment,
  applicationToDirectorScore,
  applicationToEquifaxDirectorScoreMeetsThreshold,
  applicationToFacilityEstablishmentFee,
  applicationToFacilityEstablishmentFeePercent,
  applicationToLastUpdatedByUser,
  applicationToLoanAmount,
  applicationToLoanTerms,
  applicationToLtv,
  applicationToMargin,
  applicationToPropertyOwner,
  applicationToSecurityType,
  getUser,
} from "@portal-workspace/grow-ui-library";
import {UntilDestroy} from "@ngneat/until-destroy";
import {MatTableModule} from "@angular/material/table";
import {MatCardModule} from '@angular/material/card';
import {MessageBoxComponent} from '../../message-box/message-box.component';
import {LooseCurrencyPipe} from '../../../pipes/loose-currency.pipe';
import {PercentagePipe} from '../../../pipes/percentage.pipe';

// Read-only viewer for legacy Corporate Loans pricing. The product is decommissioned;
// no recalculation or editing is supported. Values are sourced from the saved application/PricingDetails.
@UntilDestroy()
@Component({
  selector: 'corporate-loans-pricing',
  templateUrl: './corporate-loans-pricing.component.html',
  styleUrls: ['./corporate-loans-pricing.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FlexModule,
    MatTableModule,
    MatCardModule,
    MessageBoxComponent,
    LooseCurrencyPipe,
    PercentagePipe,
  ]
})
export class CorporateLoansPricingComponent implements OnInit {

  @Input({required: true}) application!: CorporateLoanApplication;

  user = getUser();
  isInternalUser = isInternalUser(this.user);

  lastEditedByUser?: string | null;

  dataSource: (
    {name: string, highlight?: (value: string | null | undefined) => boolean, type: 'text', value: string | null | undefined} |
    {name: string, highlight?: (value: number | string | null | undefined) => boolean, type: 'currency', value: number | string | null | undefined} |
    {name: string, highlight?: (value: number | string | null | undefined) => boolean, type: 'percentage', value: number | string | null | undefined}
  )[] = [];

  ngOnInit() {
    this.createReadonlyTableDataSource();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['application']) {
      this.createReadonlyTableDataSource();
    }
  }

  private createReadonlyTableDataSource() {
    this.lastEditedByUser = applicationToLastUpdatedByUser(this.application)?.name;
    const ltvType = applicationToLtv(this.application);
    const ltvLabel = LtvSelectionValueOptions.find(opt => opt.type == ltvType)?.name ?? '';
    const securityType = applicationToSecurityType(this.application);
    const securityLabel = SecurityTypeSelectionValueOptions.find(opt => opt.type == securityType)?.name ?? '';

    this.dataSource = [
      { name: 'Facility Limit', type: 'currency', value: applicationToLoanAmount(this.application) },
      { name: 'Facility Establishment Fee (%)', type: 'percentage', value: applicationToFacilityEstablishmentFeePercent(this.application)?.type },
      { name: 'Facility Establishment Fee ($)', type: 'currency', value: applicationToFacilityEstablishmentFee(this.application) },
      { name: 'Loan Terms', type: 'text', value: `${applicationToLoanTerms(this.application)?.type ?? ''} months` },
      { name: 'RBA Rate (%)', type: 'percentage', value: getRbaRate(this.application) },
      { name: 'Monthly Facility Fees ($)', type: 'currency', value: this.application.PricingDetails?.Repayment ?? 0 },
      { name: 'Brokerage (%)', type: 'percentage', value: getBrokerage(this.application) },
      { name: 'Brokerage (ex GST)', type: 'currency', value: applicationToBrokerageAmount(this.application) },
      { name: 'Property Owner', type: 'text', value: booleanToYesNo(applicationToPropertyOwner(this.application)) },
      { name: 'Adverse on file', type: 'text', value: booleanToYesNo(applicationToAdverseOnFile(this.application)) },
      { name: 'Director Equifax Score > 600', type: 'text', value: booleanToYesNo(applicationToEquifaxDirectorScoreMeetsThreshold(this.application, '>=600')) },
      { name: 'Director Score 500-550', type: 'text', value: booleanToYesNo(applicationToDirectorScore(this.application)) },
      { name: 'LTV', type: 'text', value: ltvLabel },
      { name: 'Security Type', type: 'text', value: securityLabel },
      { name: 'Doc/Annual Fee ($)', type: 'currency', value: getDocFee(this.application) },
      { name: 'Margin (%)', type: 'percentage', value: applicationToMargin(this.application) },
      { name: 'Interest Rate (%)', type: 'percentage', value: getInterestRate(this.application) },
      { name: 'Credit Rate Adjustment', highlight: (v) => Number(v) != 0, type: 'percentage', value: applicationToCrediRateAdjustment(this.application) },
    ];
  }
}
